import { FC, useState, useRef, ChangeEvent, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import errorIcon from 'assets/vectors/error-icon.svg';
import calendarIcon from 'assets/vectors/calendar-icon.svg';
import showPasswordIcon from 'assets/vectors/password-show.svg';
import hidePasswordIcon from 'assets/vectors/password-hide.svg';

import { useDetectOutsideClick } from 'hooks/useDetectOutsideClick';
import DatePicker from 'components/UI/DatePicker';

import {
  Container,
  InputContainer,
  InputLabel,
  Input,
  InputButton,
  ErrorIcon,
  ErrorMessage,
  InputLabelNoDescription,
} from './styled';

type CustomIcon = {
  src: any;
  alt: string;
  onClick: () => void;
};

type Props = {
  description?: string;
  placeholder?: string;
  type?: string;
  register?: any;
  defaultValue?: string;
  disabled?: boolean;
  tight?: boolean;
  error?: any;
  icon?: CustomIcon;
  maxDate?: Date;
  minWidth?: number;
  uppercase?: boolean;
  setDate?: (date: Date) => void;
};

const InputField: FC<Props> = ({
  description,
  register,
  type,
  defaultValue,
  disabled = false,
  tight = false,
  placeholder = ' ',
  error,
  icon,
  maxDate,
  minWidth,
  uppercase,
  setDate,
}) => {
  // Refs
  const calendarRef = useRef<HTMLDivElement>(null);
  const calendarButtonRef = useRef<HTMLButtonElement>(null);

  // State
  const [focus, setFocus] = useState<boolean>(false);
  const [passwordShow, setPasswordShow] = useState<boolean>(false);
  const [calendarOpen, setCalendarOpen] = useDetectOutsideClick(
    [calendarRef, calendarButtonRef],
    false
  );

  // Handle focus / blur
  const onFocus = useCallback(() => setFocus(true), []);
  const onBlur = useCallback(() => setFocus(false), []);

  // Handle toggle calendar
  const onCalToggle = useCallback(
    () => setCalendarOpen(!calendarOpen),
    [setCalendarOpen, calendarOpen]
  );

  // Uppercase input
  const onInput = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (uppercase) {
        event.target.value = event.target.value.toLocaleUpperCase();
      }
    },
    [uppercase]
  );

  // Render input button
  const inputButton = useMemo(() => {
    if (error) {
      return <ErrorIcon src={errorIcon} alt="Error" />;
    }
    if (icon) {
      return (
        <InputButton type="button" onClick={() => icon.onClick()}>
          <img src={icon.src} alt={icon.alt} />
        </InputButton>
      );
    }
    if (type === 'password') {
      return (
        <InputButton
          type="button"
          onClick={() => setPasswordShow(!passwordShow)}
        >
          <img
            src={!passwordShow ? showPasswordIcon : hidePasswordIcon}
            alt="show/hide password"
          />
        </InputButton>
      );
    }
    if (type === 'date') {
      return (
        <InputButton
          type="button"
          ref={calendarButtonRef}
          onClick={onCalToggle}
        >
          <img src={calendarIcon} alt="calendar" />
        </InputButton>
      );
    }
    return null;
  }, [onCalToggle, error, icon, type, passwordShow]);

  // Default date
  const defaultDate = useMemo(() => {
    if (type === 'date' && defaultValue) {
      return new Date(defaultValue);
    }
  }, [type, defaultValue]);

  return (
    <Container>
      <InputContainer
        $isFocus={focus}
        $isDisabled={disabled}
        $isTight={tight}
        $isError={error}
      >
        <Input
          {...register}
          defaultValue={defaultValue}
          placeholder={placeholder}
          type={passwordShow ? 'text' : type}
          multiple={type === 'file'}
          onInput={onInput}
          onFocus={onFocus}
          onBlur={onBlur}
          $isDisabled={disabled}
          $isTight={tight}
          $minWidth={minWidth}
        />
        {description ? (
          <InputLabel $isFocus={focus} $isTight={tight} $isError={error}>
            <FormattedMessage id={description} />
          </InputLabel>
        ) : (
          <InputLabelNoDescription
            $isFocus={focus}
            $isTight={tight}
            $isError={error}
          />
        )}
        {inputButton}
      </InputContainer>
      <DatePicker
        isOpen={calendarOpen}
        refValue={calendarRef}
        defaultValue={defaultDate}
        onClose={onCalToggle}
        onChange={setDate}
        maxDate={maxDate}
      />
      <ErrorMessage $isTight={tight}>
        {error && <FormattedMessage id={`${error.message}`} />}
      </ErrorMessage>
    </Container>
  );
};

export default InputField;
